import React, {useState, useEffect, useMemo} from "react";
import {Loader, ArrowUpDown, Calendar, Eye} from "lucide-react";
import {useTranslation} from "react-i18next";
import secureStorage from "../../utils/secureStorage";
import {api} from "../../config/api";
import {useQuery} from '@tanstack/react-query';
import moment from "moment";
import EmployeeDetails from "./EmployeeDetails";
import {SearchInput, SummaryStrip, thClass} from "../../stock/StockUI";
import Button from "../Common/Button";

// Moment's locale is synced globally by useSyncMomentLocale() at the app root.

const Employees = ({_dateRange = [], _startTime, _endTime, refreshKey = 0, isDarkMode = false}) => {
    const {t} = useTranslation("activite");
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [showDetails, setShowDetails] = useState(false);

    // Memoize date range values to prevent infinite re-renders
    const dateRangeKey = useMemo(() => {
        if (!_dateRange || _dateRange.length === 0) return '';
        const range = _dateRange[0];
        const startDate = range?.startDate ? moment(range.startDate).format('YYYY-MM-DD') : '';
        const endDate = range?.endDate ? moment(range.endDate).format('YYYY-MM-DD') : '';
        const startTime = _startTime || '';
        const endTime = _endTime || '';
        return `${startDate}-${endDate}-${startTime}-${endTime}`;
    }, [_dateRange, _startTime, _endTime]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchEmployeesData = async () => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            // Build query parameters
            let url = '/activite/employee';
            const params = [];

            if (_dateRange && _dateRange.length > 0) {
                const dateRange = _dateRange[0];
                if (dateRange.startDate) {
                    params.push(`startDate=${encodeURIComponent(moment(dateRange.startDate).format('YYYY-MM-DD'))}`);
                }
                if (dateRange.endDate) {
                    params.push(`endDate=${encodeURIComponent(moment(dateRange.endDate).format('YYYY-MM-DD'))}`);
                }
            }

            if (_startTime) {
                params.push(`startTime=${encodeURIComponent(_startTime)}`);
            }

            if (_endTime) {
                params.push(`endTime=${encodeURIComponent(_endTime)}`);
            }

            if (debouncedSearchTerm) {
                params.push(`search=${encodeURIComponent(debouncedSearchTerm)}`);
            }

            if (params.length > 0) {
                url += `?${params.join('&')}`;
            }

            const response = await api.get(url, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                t("employees.fetchError");
            throw new Error(errorMessage);
        }
    };

    const {
        data: employeesData,
        isLoading,
        error
    } = useQuery({
        queryKey: ['employees-activity', dateRangeKey, debouncedSearchTerm, refreshKey],
        queryFn: fetchEmployeesData,
        keepPreviousData: true
    });

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return "0.00";
        return parseFloat(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    };

    // Calculate totals
    const totals = useMemo(() => {
        if (!employeesData?.data) return {salesCount: 0, totalAmount: 0};

        return employeesData.data.reduce((acc, employee) => {
            acc.salesCount += employee.salesCount;
            acc.totalAmount += employee.totalAmount;
            return acc;
        }, {salesCount: 0, totalAmount: 0});
    }, [employeesData]);

    // Sort functionality
    const [sortConfig, setSortConfig] = useState({key: 'totalAmount', direction: 'desc'});

    const sortedEmployees = useMemo(() => {
        if (!employeesData?.data) return [];

        const sortableEmployees = [...employeesData.data];
        sortableEmployees.sort((a, b) => {
            if (a[sortConfig.key] < b[sortConfig.key]) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (a[sortConfig.key] > b[sortConfig.key]) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
        return sortableEmployees;
    }, [employeesData, sortConfig]);

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({key, direction});
    };

    const handleViewDetails = (employee) => {
        setSelectedEmployee(employee);
        setShowDetails(true);
    };

    const handleBackToList = () => {
        setShowDetails(false);
        setSelectedEmployee(null);
    };

    if (isLoading) return (
        <div className="flex justify-center items-center h-40">
            <Loader className="w-8 h-8 text-primary animate-spin"/>
        </div>
    );

    if (error) return (
        <div className="p-4 text-center text-red-500">
            {t("employees.errorPrefix", {message: error.message})}
        </div>
    );

    // Si on affiche les détails d'un employé
    if (showDetails && selectedEmployee) {
        return (
            <EmployeeDetails
                employeeId={selectedEmployee.employeeId}
                employeeName={selectedEmployee.name}
                onBack={handleBackToList}
                dateRange={_dateRange}
                startTime={_startTime}
                endTime={_endTime}
                isDarkMode={isDarkMode}
            />
        );
    }

    // Affichage de la liste des employés
    return (
        <div className={`text-sm ${isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'}`}>
            <div className="w-full p-0">
                <div>
                    {/* Même barre de recherche que le Stock / catalogue */}
                    <div className="flex">
                        <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder={t("employees.searchPlaceholder")}/>
                    </div>

                    {/* Totaux : même bande à filets que le Stock, collée à la recherche et au tableau */}
                    <SummaryStrip items={[
                        {key: "employees", label: t("employees.totalEmployees"), value: employeesData?.data?.length || 0},
                        {key: "sales", label: t("employees.totalSales"), value: totals.salesCount},
                        {key: "revenue", label: t("employees.revenue"), value: `${formatCurrency(totals.totalAmount)} DH`, color: "text-primary-dark dark:text-primary"},
                    ]}/>

                    {sortedEmployees.length === 0 ? (
                        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 text-center">
                            <p className="text-gray-500 dark:text-gray-400">
                                {debouncedSearchTerm ?
                                    t("employees.noSearchResults") :
                                    t("employees.noData")}
                            </p>
                            <div className="flex items-center justify-center mt-3 text-gray-400 dark:text-gray-500">
                                <Calendar className="w-5 h-5 mr-2"/>
                                <p>{t("employees.tryOtherDateRange")}</p>
                            </div>
                        </div>
                    ) : (
                        <>
                        {/* Mobile cards */}
                        <div className="sm:hidden divide-y divide-gray-200 dark:divide-gray-700 border-b border-gray-200 dark:border-gray-700">
                            {sortedEmployees.map((employee) => (
                                <div
                                    key={employee.employeeId}
                                    className="bg-white dark:bg-gray-800 p-4"
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center min-w-0">
                                            <div
                                                className="flex-shrink-0 h-8 w-8 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center text-primary">
                                                {employee.name ? employee.name.charAt(0) : ''}
                                            </div>
                                            <div className="ml-3 min-w-0">
                                                <div
                                                    className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                                    {employee.name}
                                                </div>
                                            </div>
                                        </div>
                                        <Button variant="tinted" size="sm" icon={Eye} title={t("employees.viewDetails")}
                                                onClick={() => handleViewDetails(employee)}>
                                            {t("employees.details")}
                                        </Button>
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                        <div className="text-gray-500 dark:text-gray-400">
                                            {t("employees.salesCount", {count: employee.salesCount})}
                                        </div>
                                        <div className="font-semibold text-gray-900 dark:text-gray-100">
                                            {formatCurrency(employee.totalAmount)} DH
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <div className="bg-gray-50 dark:bg-gray-700 p-4 flex items-center justify-between text-sm font-bold text-gray-700 dark:text-gray-200">
                                <span>{t("employees.total")}</span>
                                <span>{t("employees.salesCount", {count: totals.salesCount})} · {formatCurrency(totals.totalAmount)} DH</span>
                            </div>
                        </div>

                        {/* Desktop table */}
                        <div className="hidden sm:block bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                                <tr>
                                    <th className={thClass}>
                                        <button
                                            onClick={() => requestSort('name')}
                                            className="flex items-center focus:outline-none"
                                        >
                                            {t("employees.columns.employee")}
                                            {sortConfig.key === 'name' && (
                                                <ArrowUpDown className="ml-1 w-3 h-3"/>
                                            )}
                                        </button>
                                    </th>
                                    <th className={`${thClass} text-right`}>
                                        <button
                                            onClick={() => requestSort('salesCount')}
                                            className="flex items-center justify-end focus:outline-none ml-auto"
                                        >
                                            {t("employees.columns.salesCount")}
                                            {sortConfig.key === 'salesCount' && (
                                                <ArrowUpDown className="ml-1 w-3 h-3"/>
                                            )}
                                        </button>
                                    </th>
                                    <th className={`${thClass} text-right`}>
                                        <button
                                            onClick={() => requestSort('totalAmount')}
                                            className="flex items-center justify-end focus:outline-none ml-auto"
                                        >
                                            {t("employees.columns.revenue")}
                                            {sortConfig.key === 'totalAmount' && (
                                                <ArrowUpDown className="ml-1 w-3 h-3"/>
                                            )}
                                        </button>
                                    </th>
                                    <th className={thClass}><span className="sr-only">{t("employees.columns.actions")}</span></th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {sortedEmployees.map((employee) => (
                                    <tr
                                        key={employee.employeeId}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150"
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div
                                                    className="flex-shrink-0 h-8 w-8 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center text-primary">
                                                    {employee.name ? employee.name.charAt(0) : ''}
                                                </div>
                                                <div className="ml-4">
                                                    <div
                                                        className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                        {employee.name}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 text-right tabular-nums">
                                            {employee.salesCount}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 text-right font-semibold tabular-nums">
                                            {formatCurrency(employee.totalAmount)} DH
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <Button variant="tinted" size="sm" icon={Eye} title={t("employees.viewDetails")}
                                                    onClick={() => handleViewDetails(employee)}>
                                                {t("employees.details")}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                                <tfoot className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                    <td className="px-6 py-3 whitespace-nowrap text-sm font-bold text-gray-700 dark:text-gray-200">
                                        {t("employees.total")}
                                    </td>
                                    <td className="px-6 py-3 whitespace-nowrap text-sm font-bold text-gray-700 dark:text-gray-200 text-right">
                                        {totals.salesCount}
                                    </td>
                                    <td className="px-6 py-3 whitespace-nowrap text-sm font-bold text-gray-700 dark:text-gray-200 text-right">
                                        {formatCurrency(totals.totalAmount)} DH
                                    </td>
                                    <td className="px-6 py-3 whitespace-nowrap"></td>
                                </tr>
                                </tfoot>
                            </table>
                        </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Employees;
